import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Palette, Check, Sun, Moon, Leaf, Crown, Sparkles, Heart } from 'lucide-react';
import { toast } from 'sonner';

const PRESET_ICONS = {
  sunset_orange: Sun,
  ocean_blue: Sparkles,
  forest_green: Leaf,
  royal_purple: Crown,
  midnight_dark: Moon,
  rose_gold: Heart
};

export default function ThemeSettings({ onThemeChange }) {
  const [presets, setPresets] = useState({});
  const [currentTheme, setCurrentTheme] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('sunset_orange');
  const [customColors, setCustomColors] = useState({
    primary: '#f97316',
    accent: '#a855f7',
    background: '#fff7ed',
    card_bg: '#ffffff',
    text_primary: '#1f2937',
    text_secondary: '#6b7280',
    sidebar_bg: '#ffffff',
    sidebar_text: '#374151'
  });
  const [isCustom, setIsCustom] = useState(false);
  const [saving, setSaving] = useState(false);

  // Function to apply theme colors to CSS variables and DOM
  const applyThemeToCSS = (theme) => {
    const root = document.documentElement;
    
    // Set CSS variables
    if (theme.primary) {
      root.style.setProperty('--theme-primary', theme.primary);
    }
    if (theme.accent) {
      root.style.setProperty('--theme-accent', theme.accent);
    }
    if (theme.background) {
      root.style.setProperty('--theme-background', theme.background);
    }
    if (theme.card_bg) {
      root.style.setProperty('--theme-card-bg', theme.card_bg);
    }
    if (theme.text_primary) {
      root.style.setProperty('--theme-text-primary', theme.text_primary);
    }
    if (theme.text_secondary) {
      root.style.setProperty('--theme-text-secondary', theme.text_secondary);
    }
    if (theme.sidebar_bg) {
      root.style.setProperty('--theme-sidebar-bg', theme.sidebar_bg);
    }
    if (theme.sidebar_text) {
      root.style.setProperty('--theme-sidebar-text', theme.sidebar_text);
    }
    
    // Apply background color to body and main container
    if (theme.background) {
      document.body.style.backgroundColor = theme.background;
      const mainDiv = document.querySelector('.bg-gradient-hero');
      if (mainDiv) mainDiv.style.backgroundColor = theme.background;
    }
    
    // Apply sidebar colors
    if (theme.sidebar_bg) {
      const sidebar = document.querySelector('aside');
      if (sidebar) {
        sidebar.style.backgroundColor = theme.sidebar_bg;
      }
    }
    
    // Apply primary color to gradient buttons
    const gradientBtns = document.querySelectorAll('.bg-gradient-orange');
    gradientBtns.forEach(btn => {
      if (theme.primary) {
        btn.style.background = `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primary}dd 100%)`;
      }
    });
    
    // Store in localStorage for persistence
    localStorage.setItem('currentTheme', JSON.stringify(theme));
    
    // Notify parent
    if (onThemeChange) {
      onThemeChange(theme);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [presetsRes, themeRes] = await Promise.all([
        api.get('/theme/presets'),
        api.get('/theme')
      ]);
      setPresets(presetsRes.data);
      setCurrentTheme(themeRes.data);
      setSelectedPreset(themeRes.data.preset || 'sunset_orange');
      setIsCustom(themeRes.data.preset === 'custom');
      if (themeRes.data.preset === 'custom') {
        setCustomColors({
          primary: themeRes.data.primary || '#f97316',
          accent: themeRes.data.accent || '#a855f7',
          background: themeRes.data.background || '#fff7ed',
          card_bg: themeRes.data.card_bg || '#ffffff',
          text_primary: themeRes.data.text_primary || '#1f2937',
          text_secondary: themeRes.data.text_secondary || '#6b7280',
          sidebar_bg: themeRes.data.sidebar_bg || '#ffffff',
          sidebar_text: themeRes.data.sidebar_text || '#374151'
        });
      }
    } catch (error) {
      toast.error('Failed to load theme settings');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = isCustom 
        ? { preset: 'custom', ...customColors }
        : { preset: selectedPreset };
      
      await api.put('/theme', payload);
      
      // Apply the theme immediately
      const themeColors = isCustom ? customColors : presets[selectedPreset];
      if (themeColors) {
        applyThemeToCSS({ ...themeColors, preset: isCustom ? 'custom' : selectedPreset });
      }
      
      toast.success('Theme applied successfully!');
      loadData();
    } catch (error) {
      toast.error('Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const PreviewCard = ({ colors }) => (
    <div 
      className="rounded-xl p-4 border shadow-sm transition-all"
      style={{ 
        backgroundColor: colors.card_bg,
        borderColor: colors.primary + '30'
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: colors.primary + '20' }}
        >
          <Palette className="w-4 h-4" style={{ color: colors.primary }} />
        </div>
        <div>
          <p className="font-semibold text-sm" style={{ color: colors.text_primary }}>Sample Card</p>
          <p className="text-xs" style={{ color: colors.text_secondary }}>Preview text</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button 
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
          style={{ backgroundColor: colors.primary }}
        >
          Primary
        </button>
        <button 
          className="px-3 py-1.5 rounded-lg text-xs font-medium text-white"
          style={{ backgroundColor: colors.accent }}
        >
          Accent
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="theme-settings">
      <div>
        <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>
          Theme Settings
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Customize your hotel's branding and color scheme
        </p>
      </div>

      {/* Theme Presets */}
      <Card className="border-orange-100">
        <CardHeader>
          <CardTitle className="text-gray-800 flex items-center gap-2">
            <Palette className="h-5 w-5 text-orange-500" />
            Theme Presets
          </CardTitle>
          <CardDescription>Choose a preset theme or create your own custom colors</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.entries(presets).map(([key, preset]) => {
              const Icon = PRESET_ICONS[key] || Palette;
              const isSelected = !isCustom && selectedPreset === key;
              return (
                <button
                  key={key}
                  onClick={() => { setSelectedPreset(key); setIsCustom(false); }}
                  className={`relative p-4 rounded-xl border-2 transition-all hover:scale-105 ${
                    isSelected 
                      ? 'border-orange-500 shadow-lg' 
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                  style={{ backgroundColor: preset.background }}
                  data-testid={`preset-${key}`}
                >
                  {isSelected && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div 
                    className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: preset.primary }}
                  >
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-xs font-medium text-center" style={{ color: preset.text_primary }}>
                    {preset.name}
                  </p>
                  <div className="flex justify-center gap-1 mt-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.primary }} />
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.accent }} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Custom Option */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => setIsCustom(true)}
              className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                isCustom 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-200 hover:border-orange-300'
              }`}
              data-testid="custom-theme-btn"
            >
              <Palette className="w-6 h-6 text-orange-500" />
              <div className="text-left">
                <p className="font-medium text-gray-800">Custom Theme</p>
                <p className="text-sm text-gray-500">Create your own color scheme</p>
              </div>
              {isCustom && <Badge className="ml-auto bg-orange-100 text-orange-700">Selected</Badge>}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Custom Colors Editor */}
      {isCustom && (
        <Card className="border-orange-100">
          <CardHeader>
            <CardTitle className="text-gray-800">Custom Colors</CardTitle>
            <CardDescription>Define your own color palette</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'primary', label: 'Primary Color' },
                { key: 'accent', label: 'Accent Color' },
                { key: 'background', label: 'Background' },
                { key: 'card_bg', label: 'Card Background' },
                { key: 'text_primary', label: 'Primary Text' },
                { key: 'text_secondary', label: 'Secondary Text' },
                { key: 'sidebar_bg', label: 'Sidebar Background' },
                { key: 'sidebar_text', label: 'Sidebar Text' }
              ].map(({ key, label }) => (
                <div key={key} className="space-y-2">
                  <Label className="text-gray-700 text-sm">{label}</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={customColors[key]}
                      onChange={(e) => setCustomColors({ ...customColors, [key]: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                    />
                    <Input
                      value={customColors[key]}
                      onChange={(e) => setCustomColors({ ...customColors, [key]: e.target.value })}
                      className="flex-1 font-mono text-sm"
                      data-testid={`color-${key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      <Card className="border-orange-100">
        <CardHeader>
          <CardTitle className="text-gray-800">Preview</CardTitle>
          <CardDescription>See how your theme will look</CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className="p-6 rounded-xl"
            style={{ 
              backgroundColor: isCustom ? customColors.background : presets[selectedPreset]?.background 
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PreviewCard colors={isCustom ? customColors : presets[selectedPreset] || customColors} />
              <div 
                className="rounded-xl p-4"
                style={{ 
                  backgroundColor: isCustom ? customColors.sidebar_bg : presets[selectedPreset]?.sidebar_bg 
                }}
              >
                <p 
                  className="font-semibold mb-2"
                  style={{ color: isCustom ? customColors.sidebar_text : presets[selectedPreset]?.sidebar_text }}
                >
                  Sidebar Preview
                </p>
                <div className="space-y-2">
                  {['Dashboard', 'Rooms', 'Guests'].map(item => (
                    <div 
                      key={item}
                      className="px-3 py-2 rounded-lg text-sm"
                      style={{ 
                        backgroundColor: (isCustom ? customColors.primary : presets[selectedPreset]?.primary) + '15',
                        color: isCustom ? customColors.sidebar_text : presets[selectedPreset]?.sidebar_text
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-gradient-orange hover:opacity-90 text-white px-8"
          data-testid="save-theme-btn"
        >
          {saving ? 'Saving...' : 'Save Theme'}
        </Button>
      </div>
    </div>
  );
}
